import { useMemo } from 'react';
import { CoinsIcon, SearchXIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';

import { PeriodStepper, isForbidden } from '@/features/workspace-usage';
import { EmptyState } from '@/shared/components/EmptyState';
import { RetryButton } from '@/shared/components/RetryButton';
import { usePendingDisplay } from '@/shared/hooks/usePendingDisplay';

import { ADMIN_PAGE_SECTION } from '../adminLayout';
import { CONSOLE_ANSWER_COPY as ANSWER } from '../consoleCopy';
import { ConsoleAnswer, ConsoleHeader } from './ConsoleHeader';
import { useWorkspaceLimits } from '../hooks/useWorkspaceLimits';
import { parseSort, sortParam, type LimitsSearch } from '../searchParams';
import {
  LIMITS_FILTERS,
  matchesFilter,
  matchesQuery,
  sortRows,
  type LimitsFilter,
  type LimitsSort,
} from '../workspaceLimits';
import { LimitsTableSkeleton } from './AdminSkeletons';
import { LimitsPolicyCards } from './LimitsPolicyCards';
import { LimitsToolbar } from './LimitsToolbar';
import { WorkspaceLimitsSheet } from './WorkspaceLimitsSheet';
import { WorkspaceLimitsTable } from './WorkspaceLimitsTable';

/** The parts of the address this page reads beyond the period. */
export type LimitsView = Pick<
  LimitsSearch,
  'q' | 'filter' | 'sort' | 'workspace'
>;

/**
 * Every limit in the organization, one row per workspace (spec 117).
 *
 * The only place a limit changes. It used to be four sections — the spend default, a
 * spend table, workflow caps and run capacity — each listing workspaces its own way, so
 * the question an administrator actually asks ("why is this workspace held back?") took
 * three tables to answer. Now the organization's rules sit in three cards, every
 * workspace appears once with everything that holds it back, and a sheet changes one
 * workspace's limits in one place.
 */
export interface AdminLimitsPageProps {
  /** The period whose spend the table shows, or `undefined` for the current. */
  period?: string;
  onPeriodChange: (periodKey: string | undefined) => void;
  view?: LimitsView;
  onViewChange?: (next: LimitsView) => void;
}

export function AdminLimitsPage({
  period,
  onPeriodChange,
  view = {},
  onViewChange,
}: AdminLimitsPageProps) {
  const { usage, rows, policy, hasRuns, hasMemory } =
    useWorkspaceLimits(period);
  const showPlaceholder = usePendingDisplay(usage.isPending);
  const sort = parseSort(view.sort);
  const filter: LimitsFilter = view.filter ?? 'all';
  const query = view.q ?? '';

  // Counted before the search narrows anything, so a chip says what it would keep.
  const counts = useMemo(() => {
    const result = {} as Record<LimitsFilter, number>;
    for (const option of LIMITS_FILTERS) {
      result[option] = rows.filter((row) => matchesFilter(row, option)).length;
    }
    return result;
  }, [rows]);

  const shown = useMemo(
    () =>
      sortRows(
        rows.filter(
          (row) => matchesFilter(row, filter) && matchesQuery(row, query),
        ),
        sort,
      ),
    [rows, filter, query, sort],
  );

  const update = (next: Partial<LimitsView>) => {
    onViewChange?.({ ...view, ...next });
  };

  // The frame is rendered unconditionally (spec 079 FR-005): its words are known before
  // the request is made, and replacing them with grey bars claimed otherwise.
  return (
    <section className={ADMIN_PAGE_SECTION}>
      <ConsoleHeader
        title="Limits"
        description="What every workspace may spend and how many runs it may have at once — one row each, changed in one place."
        answer={
          usage.data ? (
            <ConsoleAnswer
              figure={String(counts.all)}
              words={ANSWER.limitsWords(counts.all)}
              detail={`${ANSWER.custom(counts.custom)} · ${ANSWER.nearSpend(counts['near-spend'])} · ${ANSWER.runFull(counts['run-full'])}`}
            />
          ) : undefined
        }
        actions={
          usage.data ? (
            // Governs the spend figures only; run figures are always "now" (FR-003).
            // A phone's full width, arrows at its ends (docs/phone.md PH-03).
            <div data-phone-row className="max-md:w-full">
              <PeriodStepper
                period={usage.data.period}
                earliestPeriodKey={usage.data.earliest_period_key}
                onChange={onPeriodChange}
              />
            </div>
          ) : undefined
        }
      />

      {usage.isPending ? (
        showPlaceholder ? (
          <LimitsTableSkeleton />
        ) : null
      ) : usage.isError ? (
        <EmptyState
          icon={CoinsIcon}
          description={
            isForbidden(usage.error)
              ? 'Only an organization administrator can set limits.'
              : 'These limits could not be read just now.'
          }
          action={
            isForbidden(usage.error) ? undefined : (
              <RetryButton
                onRetry={() => void usage.refetch()}
                isFetching={usage.isFetching}
              />
            )
          }
        />
      ) : policy ? (
        <>
          <LimitsPolicyCards
            policy={policy}
            summary={usage.data}
            hasRuns={hasRuns}
          />
          <LimitsToolbar
            query={query}
            onQueryChange={(next) => {
              update({ q: next === '' ? undefined : next });
            }}
            filter={filter}
            onFilterChange={(next) => {
              update({ filter: next === 'all' ? undefined : next });
            }}
            counts={counts}
            hasRuns={hasRuns}
          />
          {shown.length === 0 ? (
            <EmptyState
              icon={SearchXIcon}
              description="No workspace matches."
              action={
                <Button
                  variant="outline"
                  onClick={() => {
                    update({ q: undefined, filter: undefined });
                  }}
                >
                  Clear search and filter
                </Button>
              }
            />
          ) : (
            <WorkspaceLimitsTable
              rows={shown}
              hasRuns={hasRuns}
              hasMemory={hasMemory}
              selectedId={view.workspace}
              onSelect={(workspaceId) => {
                update({ workspace: workspaceId });
              }}
              sort={sort}
              onSortChange={(next: LimitsSort | undefined) => {
                update({ sort: next ? sortParam(next) : undefined });
              }}
            />
          )}
          <WorkspaceLimitsSheet
            // A deleted workspace has no limits to change (spec 146), even when the
            // address names it.
            row={rows.find(
              (row) => row.workspaceId === view.workspace && !row.deleted,
            )}
            policy={policy}
            currency={usage.data.spend.currency ?? 'USD'}
            hasRuns={hasRuns}
            hasMemory={hasMemory}
            usageRows={usage.data.workspaces}
            onClose={() => {
              update({ workspace: undefined });
            }}
          />
        </>
      ) : null}
    </section>
  );
}
